import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageRenderer from "@/components/PageRenderer";
import { ShieldCheck, Lock, Cookie } from "lucide-react";

const CONTENT = {
  terms: {
    icon: ShieldCheck,
    eyebrow: "Legal",
    title: "Terms & Conditions",
    updated: "Last updated: June 2026",
    sections: [
      ["Acceptance of Terms", "By using Pavitra Mantra to discover Mandirs and book Worships or Vedic Rituals, you agree to these Terms & Conditions. If you do not agree, please do not use the platform."],
      ["Bookings & Purohits", "All worships and rituals are performed by Purohits who are verified by our team before appearing in the booking flow. Timings and availability are indicative and may be adjusted by the Mandir or Purohit for auspicious muhurat."],
      ["Payments", "Prices are shown in Indian Rupees (₹) and include applicable taxes. Payment is processed securely through our payment gateway. A booking is confirmed only after successful payment."],
      ["Cancellations", "You may cancel an upcoming booking from the My Bookings section. Refund eligibility, where applicable, follows the cancellation policy communicated at the time of booking."],
      ["Illustrative Vedic Readings", "Panchang, Kundli, Dosha and Matchmaking readings are curated, illustrative samples for guidance and are not a substitute for a personalised consultation with a Purohit."],
    ],
  },
  privacy: {
    icon: Lock,
    eyebrow: "Legal",
    title: "Privacy Policy",
    updated: "Last updated: June 2026",
    sections: [
      ["Information We Collect", "We collect your name, email and profile picture through Google sign-in, and the devotee details you provide when booking (name, phone, email and optional address for prasad delivery)."],
      ["How We Use It", "Your information is used solely to fulfil your bookings, assign a Purohit, send confirmations and reminders, and improve your experience on Pavitra Mantra."],
      ["Data Sharing", "We share only the details necessary to perform your worship with the assigned Mandir and Purohit. We never sell your personal data."],
      ["Communications", "We send transactional emails such as booking confirmations and day-before reminders. We never ask for your password or card details over email."],
      ["Your Rights", "You may request access to or deletion of your personal data at any time by contacting seva@pavitramantra.in."],
    ],
  },
  cookies: {
    icon: Cookie,
    eyebrow: "Legal",
    title: "Cookies Settings",
    updated: "Last updated: June 2026",
    sections: [
      ["What Cookies We Use", "Pavitra Mantra uses a single essential, secure session cookie to keep you signed in after Google authentication. This cookie is required for the platform to function."],
      ["Analytics & Preferences", "We may use minimal preference storage (such as your light/dark theme choice) kept locally in your browser. These do not track you across other websites."],
      ["Managing Cookies", "You can clear cookies at any time through your browser settings. Removing the session cookie will simply sign you out of Pavitra Mantra."],
      ["No Third-Party Advertising", "We do not use advertising or cross-site tracking cookies."],
    ],
  },
};

export default function Legal() {
  const { doc } = useParams();
  const [cmsPage, setCmsPage] = useState(undefined);
  useEffect(() => { setCmsPage(undefined); api.get(`/pages/legal-${doc}`).then((r) => setCmsPage(r.data)).catch(() => setCmsPage(null)); }, [doc]);
  if (cmsPage) return <PageRenderer page={cmsPage} />;
  const data = CONTENT[doc] || CONTENT.terms;
  const Icon = data.icon;

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-12 lg:py-16" data-testid={`legal-${doc}-page`}>
      <span className="h-12 w-12 grid place-items-center rounded-2xl bg-gradient-to-br from-accent/20 to-primary/20 text-primary mb-5"><Icon className="h-6 w-6" /></span>
      <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2">{data.eyebrow}</p>
      <h1 className="font-serif text-3xl lg:text-4xl font-bold">{data.title}</h1>
      <p className="text-sm text-muted-foreground mt-2">{data.updated}</p>

      <div className="mt-8 space-y-7">
        {data.sections.map(([heading, body], i) => (
          <section key={heading} data-testid={`legal-section-${i}`}>
            <h2 className="font-serif text-lg font-semibold mb-2">{heading}</h2>
            <p className="text-muted-foreground leading-relaxed">{body}</p>
          </section>
        ))}
      </div>

      <p className="text-sm text-muted-foreground mt-10 pt-6 border-t border-border">
        Questions about this page? Write to us at <a href="mailto:seva@pavitramantra.in" className="text-primary hover:underline">seva@pavitramantra.in</a>.
      </p>
    </div>
  );
}
